"use client";

import { getMediaUrl } from "@/utils";
import ResponsiveImage from "@/components/ResponsiveImage";
import dynamic from "next/dynamic";
import Swiper from "@/components/Swiper/Swiper";
import useIsInteractive from "@/hooks/useIsInteractive";
import { useRef } from "react";
import generateSrcSet from "@/utils/generateSrcSet";

const FancyboxStyles = dynamic(() => import("@/components/FancyboxStyles"), {
  ssr: false,
});

const Gallery = ({ data, header, video, primaryImageProps = {}, secondaryImageProps = {} }) => {
  const videoRef = useRef(null);

  useIsInteractive(() => {
    if (!video) return;
    videoRef.current.src = video;
    videoRef.current.play();
  });

  if (!data?.length) data = [{ src: "" }];

  return (
    <>
      <FancyboxStyles />
      <div className="md:hidden relative">
        {header}
        <Swiper className="image-swiper" breakpoints={{}}>
          {data.map((image, index) => (
            <ResponsiveImage
              file={image}
              asBackground
              component="a"
              className={`rounded block w-full pt-[70%]`}
              key={index}
              href={getMediaUrl(image, "xlarge")}
              data-thumb={getMediaUrl(image, "thumbnail")}
              data-srcset={generateSrcSet(image, "xlarge")}
              data-fancybox="gallery-m"
              sizes="100vw"
              {...secondaryImageProps}
              // Only the first slide is visible on load; the rest wait for a swipe
              priority={index === 0}
              loading={index === 0 ? "eager" : "lazy"}
            >
              <span className="sr-only">
                click on the image to open the gallery
              </span>
            </ResponsiveImage>
          ))}
        </Swiper>
      </div>
      <div className="hidden h-[60vh] gap-4 md:flex flex-row relative">
        <div className="flex-1 relative bg-img rounded overflow-clip">
          {header}
          {!!video && (
            <div className="w-full inset-0 absolute">
              <video
                playsInline
                autoPlay
                controls
                muted
                loop
                ref={videoRef}
                className="absolute inset-0 size-full object-cover"
              >
                <source src="" type="video/mp4" />
              </video>
            </div>
          )}

          {!video && (
            <ResponsiveImage
              file={data[0]}
              asBackground
              component="a"
              href={getMediaUrl(data[0], "xlarge")}
              data-thumb={getMediaUrl(data[0], "thumbnail")}
              data-srcset={generateSrcSet(data[0], "xlarge")}
              className="absolute inset-0 size-full flex"
              data-fancybox="gallery"
              sizes="(min-width: 768px) 66vw, 100vw"
              // Desktop LCP image. Lazy + high priority instead of a preload: this
              // column is display:none on phones, where a lazy image is never fetched
              loading="lazy"
              fetchPriority="high"
              {...primaryImageProps}
            >
              <span className="sr-only">
                click on the image to open the gallery
              </span>
            </ResponsiveImage>
          )}
        </div>

        {data.length > 1 && (
          <div className="md:w-1/3 gap-4 h-full flex flex-col">
            {data.slice(!!video ? 0 : 1).map((img, index) => {
              const isHidden = index >= 2;
              return (
                <ResponsiveImage
                  file={img}
                  asBackground
                  component="a"
                  className={`block flex-grow rounded ${
                    isHidden ? "hidden" : ""
                  }`}
                  key={index}
                  data-fancybox="gallery"
                  href={getMediaUrl(img, "xlarge")}
                  data-thumb={getMediaUrl(img, "thumbnail")}
                  data-srcset={generateSrcSet(img, "xlarge")}
                  sizes="(min-width: 768px) 33vw, 100vw"
                  // lazy: never fetched on phones, where this column is hidden
                  loading="lazy"
                  {...secondaryImageProps}
                >
                  <span className="sr-only">
                    click on the image to open the gallery
                  </span>
                </ResponsiveImage>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
export default Gallery;
